import React from "react";
import { Box, Stack, Table, TableBody, TableCell, TableHead, TableRow, Typography } from "@mui/material";
import { ForumOutlined, GraphicEq, ImageOutlined, MenuBookOutlined, MovieOutlined, UndoRounded } from "@mui/icons-material";
import { Link } from "react-router-dom";
import { APP_ACCENT_GOLD, APP_BORDER, APP_TEXT_SECONDARY } from "./createShared";
import CollectionPagination from "./common/CollectionPagination";
import FableTokenAmount from "./common/FableTokenAmount";
import { formatTokens, formatUsageDay, SpendActivity } from "./usageModel";
import { UsageSkeleton } from "./common/UsageLoadingShell";

export const ACTIVITY_PAGE_SIZE = 10;

export default function UsageActivityTable({ rows, pageIndex, hasMore, loading, initialLoading = false, classic, onPrevious, onNext }: {
  rows: SpendActivity[]; pageIndex: number; hasMore: boolean; loading: boolean; classic: boolean;
  initialLoading?: boolean;
  onPrevious: () => void; onNext: () => void;
}) {
  const start = pageIndex * ACTIVITY_PAGE_SIZE;
  const visible = rows.slice(start, start + ACTIVITY_PAGE_SIZE);
  const hasNext = start + ACTIVITY_PAGE_SIZE < rows.length || hasMore;
  return <Box aria-busy={loading} role={initialLoading ? "status" : undefined} aria-label={initialLoading ? "Loading spending activity" : undefined}>
    <Table aria-label="Spending activity" sx={{ tableLayout: "fixed", borderTop: `1px solid ${APP_BORDER}`,
      "& .MuiTableCell-root": { color: "white", borderBottom: `1px solid ${APP_BORDER}`, px: { xs: 0.5, sm: 1 }, py: 2 },
      "& .MuiTableCell-head": { color: APP_TEXT_SECONDARY, fontWeight: 700, fontSize: 13, py: 1.75 },
    }}>
      <TableHead><TableRow>
        <TableCell scope="col" sx={{ width: { xs: "62%", sm: "55%" } }}>{classic ? "Story / activity" : "Activity"}</TableCell>
        <TableCell scope="col" sx={{ display: { xs: "none", sm: "table-cell" }, width: "20%" }}>Date <Box component="span" sx={{ fontWeight: 400 }}>(UTC)</Box></TableCell>
        <TableCell scope="col" align="right">Tokens</TableCell>
      </TableRow></TableHead>
      <TableBody>{initialLoading ? Array.from({ length: 5 }, (_, index) => (
        <TableRow key={index} aria-hidden="true">
          <TableCell>
            <Stack direction="row" spacing={{ xs: 1.25, sm: 2 }} alignItems="center">
              <UsageSkeleton variant="circular" width={40} height={40} sx={{ display: { xs: "none", sm: "block" }, flexShrink: 0 }} />
              <Box sx={{ width: "65%" }}>
                <UsageSkeleton width="100%" height={20} />
                <UsageSkeleton width={60} height={16} sx={{ display: { xs: "block", sm: "none" }, mt: 0.5 }} />
              </Box>
            </Stack>
          </TableCell>
          <TableCell sx={{ display: { xs: "none", sm: "table-cell" } }}><UsageSkeleton width={60} height={20} /></TableCell>
          <TableCell align="right"><UsageSkeleton width={75} height={20} sx={{ ml: "auto" }} /></TableCell>
        </TableRow>
      )) : visible.map(entry => {
        const refundOnly = entry.refunded > 0 && !entry.tokens && !entry.unpriced;
        const Icon = refundOnly ? UndoRounded : ({ Chat: ForumOutlined, Audio: GraphicEq, Images: ImageOutlined, Video: MovieOutlined })[entry.label] || MenuBookOutlined;
        return <TableRow key={entry.id} sx={{ "&:hover": { bgcolor: "rgba(255,255,255,0.025)" } }}>
          <TableCell>
            <Stack direction="row" spacing={{ xs: 1.25, sm: 2 }} alignItems="center">
              <Box aria-hidden="true" sx={{ display: { xs: "none", sm: "grid" }, placeItems: "center", flexShrink: 0,
                width: 40, height: 40, borderRadius: "50%", bgcolor: "#1B1E28", color: refundOnly ? "success.light" : "#76A9E7" }}><Icon sx={{ fontSize: 20 }} /></Box>
              <Box sx={{ minWidth: 0 }}>
                {entry.storyId ? <Typography component={Link} to={`/story-preview/${encodeURIComponent(entry.storyId)}`}
                  sx={{ color: "white", fontWeight: 700, textDecoration: "none", overflowWrap: "anywhere", "&:hover": { color: APP_ACCENT_GOLD } }}>{entry.label}</Typography>
                  : <Typography fontWeight={700} sx={{ overflowWrap: "anywhere" }}>{entry.label}</Typography>}
                <Typography fontSize={12} color={APP_TEXT_SECONDARY} sx={{ display: { xs: "block", sm: "none" }, mt: 0.5 }}>{formatUsageDay(entry.day)} · UTC</Typography>
              </Box>
            </Stack>
          </TableCell>
          <TableCell sx={{ display: { xs: "none", sm: "table-cell" } }}><Typography fontSize={14} color={APP_TEXT_SECONDARY}>{formatUsageDay(entry.day)}</Typography></TableCell>
          <TableCell align="right">
            <Stack alignItems="flex-end" spacing={0.5}>
              {!refundOnly && <Typography fontWeight={700} sx={{ fontVariantNumeric: "tabular-nums" }}>
                {entry.unpriced && !entry.tokens ? "Price pending" : <><FableTokenAmount amount={formatTokens(entry.tokens)} />
                  {entry.refunded > 0 && <Box component="span" sx={{ ml: 0.75, fontSize: 12, fontWeight: 400, color: APP_TEXT_SECONDARY }}>used</Box>}</>}
              </Typography>}
              {entry.refunded > 0 && <Box sx={{ color: "success.light" }}>
                <Typography component="div" fontWeight={700} fontSize={refundOnly ? 16 : 14}>
                  <FableTokenAmount amount={`+${formatTokens(entry.refunded)}`} />
                  {!refundOnly && <Box component="span" sx={{ ml: 0.75, fontSize: 12, fontWeight: 400 }}>refunded</Box>}
                </Typography>
                {refundOnly && <Typography fontSize={12} sx={{ mt: 0.25 }}>Refunded</Typography>}
              </Box>}
            </Stack>
          </TableCell>
        </TableRow>;
      })}
        {!initialLoading && !visible.length && <TableRow><TableCell colSpan={3}><Typography color={APP_TEXT_SECONDARY} sx={{ py: 3 }}>No spending recorded in this period.</Typography></TableCell></TableRow>}
      </TableBody>
    </Table>
    <Stack direction="row" justifyContent="space-between" alignItems="center" flexWrap="wrap" gap={2} sx={{ pt: 2.5 }}>
      <Box sx={{ ml: "auto" }}>{initialLoading ? <UsageSkeleton width={180} height={44} sx={{ borderRadius: 999 }} /> : <CollectionPagination label="Activities"
        range={rows.length ? `${start + 1}–${Math.min(start + ACTIVITY_PAGE_SIZE, rows.length)} of ${rows.length}${hasMore ? "+" : ""} activities` : "0 activities"}
        hasPrevious={pageIndex > 0} hasNext={hasNext} loading={loading} onPrevious={onPrevious} onNext={onNext} />}</Box>
    </Stack>
  </Box>;
}
